// src/utils/salesFields.js
// Single source of truth for the Sales Audit form fields (the extra details a
// salesperson fills in on top of a visitor record). The admin Visitors module
// imports this list to render a read-only view of the submitted details.
//
// Mirrors the printed "Customer Enquiry Form" (Guru Punvaanii). Each field
// renders automatically (edit on the salesperson page, read-only in the admin
// panel). Supported types:
//   'text' | 'number' | 'date' | 'tel' | 'textarea'
//   'radio'    — single choice, needs `options: []`  (rendered as checkbox tiles)
//   'checkbox' — multi choice,  needs `options: []`  (stored comma-joined)
// Extras:
//   - `{ section: '...' }` inserts a full-width sub-heading (no data)
//   - `span: true` makes a field take the full row width
//   - `required: true` blocks submit until filled
//   - `default: 'X'` preselects a radio option on fresh forms
//   - `showIf: { key, value }` shows (and requires) the field only while the
//     controlling field holds that value — see isFieldVisible()
// The values are stored together as a JSON blob in the `sales_details` column,
// so adding/removing a field here needs NO backend or database change.
//
// NOTE: the standalone Customer/sales-audit.html page keeps its own copy of this
// list (it can't import a module). If you change fields here, mirror them there.
export const SALES_FIELDS = [
  { section: 'Customer Details' },
  { key: 'gender',            label: 'Gender',            type: 'radio', options: ['Male', 'Female'], default: 'Male' },
  { key: 'age',               label: 'Age',               type: 'number', required: true },
  { key: 'profession',        label: 'Profession',        type: 'text', required: true },
  { key: 'company_name',      label: 'Name of Company',   type: 'text', required: true },
  { key: 'designation',       label: 'Designation',       type: 'text' },
  { key: 'work_location',     label: 'Work Location',     type: 'text', required: true },
  { key: 'home_town',         label: 'Customer Home Town', type: 'text' },
  { key: 'mother_tongue',     label: 'Mother Tongue',     type: 'text' },
  { key: 'family_members',    label: 'Number of Family Members', type: 'number' },
  { key: 'residing_duration', label: 'How long residing in current city', type: 'text' },
  { key: 'house_address',     label: 'House Address',      type: 'textarea', span: true, required: true },

  { section: 'Spouse Details' },
  { key: 'spouse_name',          label: 'Spouse Name',        type: 'text' },
  { key: 'spouse_gender',        label: 'Spouse Gender',      type: 'radio', options: ['Male', 'Female'], default: 'Female' },
  { key: 'spouse_age',           label: 'Spouse Age',         type: 'number' },
  { key: 'spouse_email',         label: 'Spouse Email',       type: 'text' },
  { key: 'spouse_profession',    label: 'Spouse Profession',  type: 'text' },
  { key: 'spouse_company',       label: 'Spouse Company',     type: 'text' },
  { key: 'spouse_designation',   label: 'Spouse Designation', type: 'text' },
  { key: 'spouse_work_location', label: 'Spouse Work Location', type: 'text' },
  { key: 'spouse_home_town',     label: 'Spouse Home Town',   type: 'text' },
  { key: 'spouse_mother_tongue', label: 'Spouse Mother Tongue', type: 'text' },

  { section: 'Requirement Details' },
  { key: 'purpose_of_buying', label: 'Purpose of Buying', type: 'checkbox', options: ['Own Use', 'Rental', 'Investment'], required: true },
  { key: 'source_of_funding',   label: 'Source of Funding', type: 'radio', options: ['Self Amount', 'Bank Loan'], required: true },
  { key: 'self_funding_amount', label: 'Self Funding Amount (₹)', type: 'number', required: true, showIf: { key: 'source_of_funding', value: 'Bank Loan' } },
  { key: 'bank_loan_amount',    label: 'Bank Loan Amount (₹)',    type: 'number', required: true, showIf: { key: 'source_of_funding', value: 'Bank Loan' } },
  { key: 'unit_type',         label: 'Type (Villa / Apartment)', type: 'checkbox', options: ['2 BHK', '3 BHK', '4 BHK'] },
  { key: 'preferred_floor',   label: 'Preferred Floor (if Apartment)', type: 'text' },
  { key: 'unit_dimension',    label: 'Unit Dimension',    type: 'text', required: true },
  { key: 'budget',            label: 'Budget',            type: 'text', required: true },
  { key: 'facing',            label: 'Facing',            type: 'text', required: true },
  { key: 'other_criteria',    label: 'Any Other Criteria', type: 'text', span: true },

  { section: 'Sales Executive Assessment' },
  { key: 'owns_property_bengaluru', label: 'Owns a property in Bengaluru', type: 'radio', options: ['Yes', 'No'] },
  { key: 'owns_property_details',   label: 'Property Details', type: 'text', required: true, showIf: { key: 'owns_property_bengaluru', value: 'Yes' } },
  { key: 'specific_projects_seen',  label: 'Specific project names they have seen', type: 'textarea', span: true },
  { key: 'months_exploring',        label: 'How many months exploring projects', type: 'text' },
  { key: 'comfortable_other_gpp',   label: 'Comfortable to invest in other GPP projects', type: 'radio', options: ['Yes', 'No'] },
  { key: 'interested_gpp_projects', label: 'Which GPP projects are they interested in?', type: 'text', span: true, required: true, showIf: { key: 'comfortable_other_gpp', value: 'Yes' } },
  { key: 'key_criteria',            label: 'Key Criteria of Buying', type: 'textarea', span: true },
  { key: 'decision_makers',         label: 'Who are the decision makers', type: 'text' },
  { key: 'time_spent_on_project',   label: 'How much time spent on the project', type: 'text' },
  { key: 'unit_selected',           label: 'Unit Selected', type: 'text' },
  { key: 'price_offered',           label: 'Price Offered', type: 'text' },
  { key: 'days_to_decide',          label: 'How many days required to take a decision', type: 'text' },
  { key: 'person_personality',      label: 'A little bit about person personality', type: 'textarea', span: true },
  { key: 'who_will_stay',           label: 'Who all will stay in the property after purchase', type: 'textarea', span: true },
  { key: 'support_required',        label: 'Support required from senior team for decision', type: 'textarea', span: true },
  { key: 'customer_status',         label: 'Status of Customer', type: 'radio', options: ['Hot', 'Warm', 'Cold', 'Dead'], required: true },
  { key: 'executive_remarks',       label: 'Remarks by Executive', type: 'textarea', span: true },
];

// Original visitor fields — always read-only, auto-populated from the record.
export const ORIGINAL_FIELDS = [
  { key: 'ref_number',         label: 'Ref No' },
  { key: 'customer_name',      label: 'Visitor Name' },
  { key: 'mobile',             label: 'Mobile' },
  { key: 'email',              label: 'Email' },
  { key: 'project_name',       label: 'Project' },
  { key: 'source',             label: 'Source' },
  { key: 'source_description', label: 'Source Description' },
  { key: 'reference_name',     label: 'Reference Name' },
  { key: 'reference_mobile',   label: 'Reference Mobile' },
  { key: 'reference_number',   label: 'Reference No' },
  { key: 'submit_date',        label: 'Site Visit Date' },
  { key: 'submitted_by',       label: 'Registered By' },
  { key: 'address',            label: 'Address', span: true },
  { key: 'remarks',            label: 'Remarks', span: true },
];

// Should this field appear, given the saved sales_details object? Fields with
// no showIf always show; conditional ones only when the controlling field's
// saved value matches (handles comma-joined multi values too).
export function isFieldVisible(f, sd) {
  if (!f.showIf) return true;
  const v = String(sd?.[f.showIf.key] || '').split(',').map(s => s.trim());
  return v.includes(f.showIf.value);
}

// Parse the sales_details column (JSON string or already-an-object) into a plain
// object, tolerating null/invalid values.
export function parseSalesDetails(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    const p = JSON.parse(raw);
    return p && typeof p === 'object' ? p : {};
  } catch {
    return {};
  }
}
